import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

// MotorLand: Gnadenfrist zwischen pointerleave am Menüpunkt und dem Schließen
// des Megamenüs (siehe #deactivateGraceTimer).
const DEACTIVATE_GRACE_MS = 200;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /**
   * MotorLand: Kurze Gnadenfrist vor dem Schließen nach pointerleave. Diagonale
   * Mauswege vom Trigger zum Panel können das <li> für 1–2 Pointer-Samples
   * verlassen (Streifen unter/neben der Nav-Zeile); ohne Frist schließt das
   * Megamenü sofort. Wird bei jeder (Re-)Aktivierung gelöscht.
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #deactivateGraceTimer;

  /**
   * MotorLand (19.10.2026, iPad-Megamenue): Der Elternpunkt, den der laufende Tap
   * gerade per pointerenter geoeffnet hat. Der zugehoerige click wird dann nicht
   * navigieren (erster Tap oeffnet nur, zweiter Tap folgt dem Link) — Muster wie
   * #onCascadeClick fuer die Kaskaden-Zeilen. Maus/Tastatur setzen das Feld nie.
   * @type {HTMLElement | null}
   */
  #touchOpenedItem = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
    // MotorLand (iPad-Megamenue): Tap-Verhalten der Elternpunkte + Schliessen per Tap daneben.
    this.addEventListener('click', this.#onMenuLinkClick);
    document.addEventListener('click', this.#onDocumentClick);
    this.#initCascades();
    this.#observeCascades();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('click', this.#onMenuLinkClick);
    document.removeEventListener('click', this.#onDocumentClick);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cascadeObserver?.disconnect();
    this.#cascadeObserver = null;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#deactivateGraceTimer);
    this.#deactivateGraceTimer = undefined;
    clearTimeout(this.#cascadeSwitchTimer);
    this.#cascadeSwitchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Last horizontal pointer delta, reused by the cascade flyout for aim tolerance.
   * @type {number}
   */
  #lastMovementX = 0;

  /** @type {HTMLElement | null} */
  #activeCascadeRow = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #cascadeSwitchTimer;

  /**
   * Beobachtet Nach-Injektionen des Menü-DOMs (Section-Rendering-API / Lazy-Hydration).
   * @type {MutationObserver | null}
   */
  #cascadeObserver = null;

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;
    this.#lastMovementX = event.movementX;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Wire up any Lowe's-style cascade flyouts ("Alle Produkte"). Hands hover control to JS
   * (disabling the CSS `:hover` fallback) so rows can switch panels with aim tolerance.
   */
  #initCascades() {
    this.querySelectorAll('.mega-menu--cascade').forEach((cascade) => {
      if (!(cascade instanceof HTMLElement)) return;
      // MotorLand-Fix (Bug 1): Bereits verdrahtete Kaskaden überspringen. Sonst würde
      // jeder Re-Init (siehe #observeCascades) die aktive Zeile auf die erste zurücksetzen
      // und dem Nutzer das gerade geöffnete Panel wegnehmen.
      if (cascade.dataset.jsCascade === '') return;
      cascade.dataset.jsCascade = '';
      cascade.addEventListener('pointerover', this.#onCascadePointerOver);
      cascade.addEventListener('focusin', this.#onCascadeFocusIn);
      cascade.addEventListener('pointerleave', this.#onCascadePointerLeave);
      // Tablet/touch: switch panels on tap (see #onCascadeClick), not on hover.
      cascade.addEventListener('click', this.#onCascadeClick);
      // Open the first row by default so the flyout is never empty.
      const firstRow = cascade.querySelector('.mega-menu__cascade-row');
      if (firstRow instanceof HTMLElement) this.#activateCascadeRow(firstRow);
    });
  }

  /**
   * MotorLand-Fix (Bug 1): Das „Alle Produkte"-Submenu wird beim ersten Öffnen per
   * Section-Rendering-API neu gerendert (Lazy-Hydration). Dabei wird der Kaskaden-Teilbaum
   * durch frisches Server-HTML ersetzt – ohne das per JS gesetzte `data-js-cascade` und ohne
   * die Event-Listener. Das Menü fällt dann auf den reinen CSS-Fallback zurück, der beim
   * Wandern nach rechts (Zeiger im Spalt zwischen Kategorie und Panel) das erste Panel einer
   * ANDEREN Collection einblendet – genau die gemeldete Überlagerung. Wir beobachten daher
   * den Teilbaum und verdrahten neu eingefügte Kaskaden erneut, damit `[data-active]` (JS)
   * die alleinige Sichtbarkeitsquelle bleibt und immer nur EIN Panel sichtbar ist.
   */
  #observeCascades() {
    if (this.#cascadeObserver) return;
    this.#cascadeObserver = new MutationObserver(() => this.#initCascades());
    // Nur childList/subtree: unsere eigenen Klassen-/Attribut-Wechsel (is-active, data-active)
    // triggern den Observer bewusst nicht, sonst käme es zu einer Rückkopplung.
    this.#cascadeObserver.observe(this, { childList: true, subtree: true });
  }

  /**
   * @param {PointerEvent} event
   */
  #onCascadePointerOver = (event) => {
    // On touch/tablet the panel switches on tap (#onCascadeClick), not on hover.
    if (this.#coarsePointer) return;

    const row = event.target instanceof Element ? event.target.closest('.mega-menu__cascade-row') : null;
    if (!(row instanceof HTMLElement) || row === this.#activeCascadeRow) {
      clearTimeout(this.#cascadeSwitchTimer);
      return;
    }

    // Aim tolerance: while the pointer skims rightward toward the open panel, defer the
    // switch briefly so passed-over rows don't hijack it (reuses the safety-box movement data).
    clearTimeout(this.#cascadeSwitchTimer);
    if (this.#lastMovementX > 2) {
      this.#cascadeSwitchTimer = setTimeout(() => this.#activateCascadeRow(row), HOVER_COMMIT_DELAY_MS);
    } else {
      this.#activateCascadeRow(row);
    }
  };

  /**
   * @param {FocusEvent} event
   */
  #onCascadeFocusIn = (event) => {
    const row = event.target instanceof Element ? event.target.closest('.mega-menu__cascade-row') : null;
    if (row instanceof HTMLElement) {
      clearTimeout(this.#cascadeSwitchTimer);
      this.#activateCascadeRow(row);
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #onCascadePointerLeave = (event) => {
    clearTimeout(this.#cascadeSwitchTimer);
    const cascade = event.currentTarget instanceof Element ? event.currentTarget : null;
    const firstRow = cascade?.querySelector('.mega-menu__cascade-row');
    if (firstRow instanceof HTMLElement) this.#activateCascadeRow(firstRow);
  };

  /** @type {MediaQueryList | undefined} */
  #coarsePointerQuery;

  /** Whether the primary pointer is coarse (touch). Reuses one cached MediaQueryList. */
  get #coarsePointer() {
    this.#coarsePointerQuery ??= window.matchMedia?.('(pointer: coarse)');
    return this.#coarsePointerQuery?.matches ?? false;
  }

  /**
   * Tablet/touch: first tap on a category that has a panel reveals that panel instead of
   * navigating; a second tap on the already-open row follows the link. Fine pointers (mouse)
   * ignore this and navigate immediately, since they switch panels on hover.
   * @param {MouseEvent} event
   */
  #onCascadeClick = (event) => {
    if (!this.#coarsePointer) return;
    const link = event.target instanceof Element ? event.target.closest('.mega-menu__cascade-link--parent') : null;
    if (!(link instanceof HTMLElement)) return;
    const row = link.closest('.mega-menu__cascade-row');
    if (!(row instanceof HTMLElement) || row === this.#activeCascadeRow) return;
    event.preventDefault();
    this.#activateCascadeRow(row);
  };

  /**
   * Highlight the given cascade row and reveal its matching panel.
   * @param {HTMLElement} row
   */
  #activateCascadeRow(row) {
    if (row === this.#activeCascadeRow) return;

    const cascade = row.closest('.mega-menu--cascade');
    if (!cascade) return;

    cascade.querySelectorAll('.mega-menu__cascade-row.is-active').forEach((previous) => {
      previous.classList.remove('is-active');
      previous.querySelector('.mega-menu__cascade-link')?.setAttribute('aria-expanded', 'false');
    });
    cascade.querySelectorAll('.mega-menu__cascade-panel[data-active]').forEach((panel) => {
      if (panel instanceof HTMLElement) delete panel.dataset.active;
    });

    row.classList.add('is-active');
    const index = row.dataset.cascadeIndex;
    const panel = cascade.querySelector(`.mega-menu__cascade-panel[data-cascade-index="${index}"]`);
    if (panel instanceof HTMLElement) {
      panel.dataset.active = '';
      row.querySelector('.mega-menu__cascade-link')?.setAttribute('aria-expanded', 'true');
    }

    this.#activeCascadeRow = row;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    // MotorLand: Re-Entry (z. B. über die Hover-Brücke oder das Panel) bricht
    // eine laufende Schließ-Gnadenfrist ab — auch wenn dasselbe Item aktiv bleibt.
    clearTimeout(this.#deactivateGraceTimer);
    this.#deactivateGraceTimer = undefined;

    const isMoreTrigger = event.target.slot === 'more';
    const item = findMenuItem(event.target);
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      previouslyActiveOverflowItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    // MotorLand (iPad-Megamenue): Merken, wenn ein Touch-Tap dieses Item gerade
    // geoeffnet hat — der click desselben Taps navigiert dann nicht (#onMenuLinkClick).
    // Bei bereits offenem Item greift der Early-Return oben, das Feld bleibt leer
    // und der zweite Tap folgt dem Link.
    this.#touchOpenedItem = event instanceof PointerEvent && event.pointerType === 'touch' ? item : null;
    if (overflowItem && overflowItem !== item) {
      overflowItem.ariaExpanded = 'true';
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              // Deferred hydration: recompute BOTH vars together, matching activate(). Updating only
              // --submenu-height leaves --full-open-header-height at its first-open 0, which desyncs the
              // panel geometry (top/clip-path/translateY) and closes the menu on the first pointer move.
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#setFullOpenHeaderHeight(submenu.offsetHeight, this.#getHeaderVisibleHeight());
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    // MotorLand (iPad-Megamenue): Ein Touch-Pointer verlaesst das <li> immer sofort
    // nach dem Tap (pointerleave nach pointerup) — das wuerde das gerade geoeffnete
    // Megamenue nach der Gnadenfrist wieder schliessen. Touch schliesst stattdessen
    // per Tap ausserhalb (#onDocumentClick) oder ueber einen anderen Menuepunkt.
    if (event instanceof PointerEvent && event.pointerType === 'touch') return;

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Element && Boolean(event.relatedTarget.closest('[slot="overflow"]'));

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    // MotorLand: Nicht sofort schließen. Verlässt der Zeiger das <li> nur kurz
    // (diagonaler Weg Trigger → Panel durch den Streifen unter der Nav-Zeile),
    // reaktiviert der nächste pointerenter das Item und löscht den Timer; ein
    // abgelaufener Timer schließt wie bisher über #deactivate.
    clearTimeout(this.#deactivateGraceTimer);
    const itemToDeactivate = this.#state.activeItem;
    this.#deactivateGraceTimer = setTimeout(() => {
      this.#deactivateGraceTimer = undefined;
      this.#deactivate(itemToDeactivate ?? undefined);
    }, DEACTIVATE_GRACE_MS);
  }

  /**
   * MotorLand (iPad-Megamenue): Klick-Verhalten der Elternpunkte auf Touch.
   * Hat der laufende Tap das Item gerade per pointerenter geoeffnet
   * (#touchOpenedItem), navigiert der zugehoerige click nicht; ein zweiter Tap
   * auf den offenen Punkt folgt dem Link. Kaskaden-Zeilen im Panel haben ihr
   * eigenes Verhalten (#onCascadeClick), die Maus bleibt unveraendert.
   * @param {MouseEvent} event
   */
  #onMenuLinkClick = (event) => {
    const link =
      event.target instanceof Element ? event.target.closest('a.menu-list__link[aria-haspopup="true"]') : null;
    if (!(link instanceof HTMLElement)) return;
    const openedByThisTap = this.#touchOpenedItem;
    this.#touchOpenedItem = null;
    if (openedByThisTap && openedByThisTap === link) {
      event.preventDefault();
    }
  };

  /**
   * MotorLand (iPad-Megamenue): Tap ausserhalb des Menues schliesst das offene
   * Megamenue — auf Touch gibt es kein pointerleave-Schliessen (siehe deactivate).
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Element && this.contains(event.target)) return;
    this.#deactivate();
  };

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (this.overflowListHovered || this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    if (activeOverflowItem && activeOverflowItem !== item) {
      activeOverflowItem.ariaExpanded = 'false';
    }

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
